import { useState } from "react";
import { ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";

import { useConfigurationContext } from "../root/ConfigurationContext";

/** The same service favicon source the company avatars already use. */
const faviconFor = (url: string) => {
  try {
    return `https://favicon.show/${new URL(url).hostname}`;
  } catch {
    return null;
  }
};

const ServiceIcon = ({ url, name }: { url: string; name: string }) => {
  const [failed, setFailed] = useState(false);
  const src = faviconFor(url);

  if (!src || failed) {
    return (
      <span className="size-4 rounded-sm bg-muted text-[0.6rem] font-semibold flex items-center justify-center">
        {name.charAt(0).toUpperCase()}
      </span>
    );
  }
  return (
    <img
      src={src}
      alt=""
      className="size-4 rounded-sm"
      onError={() => setFailed(true)}
    />
  );
};

/**
 * "Upload it there, then paste the link here."
 *
 * Opens the hosting service in a new tab so the flow is one click rather than
 * an instruction to read. The list is hub configuration, not a constant, so it
 * is managed from Settings without a deploy.
 *
 * This does not replace the file upload -- it sits beside it, so the bucket
 * stays for small things and anything large has an obvious somewhere-else to
 * go. That was the point: a free-tier bucket fills up quietly.
 */
export function FileHostingButtons() {
  const { fileHostingSuggestions } = useConfigurationContext();
  const services = fileHostingSuggestions ?? [];

  if (services.length === 0) return null;

  return (
    <div className="flex flex-wrap items-center gap-2">
      <span className="text-xs text-muted-foreground">Upload to</span>
      {services.map((service) => (
        <Button
          key={service.url}
          type="button"
          variant="outline"
          size="sm"
          asChild
        >
          <a href={service.url} target="_blank" rel="noreferrer noopener">
            <ServiceIcon url={service.url} name={service.name} />
            {service.name}
            <ExternalLink className="size-3 opacity-60" />
          </a>
        </Button>
      ))}
      <span className="text-xs text-muted-foreground">
        then paste the share link below
      </span>
    </div>
  );
}
